// Wishlist hearts: guests keep them in this browser (localStorage, per site address); logged-in
// shoppers in their account. After logging in, the browser's hearts are added to the account
// once and then cleared here. Without JavaScript the hearts stay hidden.

import { toast } from "./cart.js";

const API = "/api/method/softech_commerce.api.wishlist.";
const KEY = `softech:wishlist:${window.location.host}`;
const MAX = 100;

const readLocal = () => {
	try {
		const list = JSON.parse(window.localStorage.getItem(KEY) || "[]");
		return Array.isArray(list) ? list.filter((n) => typeof n === "string").slice(0, MAX) : [];
	} catch (e) {
		return [];
	}
};
const writeLocal = (list) => {
	try {
		if (list.length) window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
		else window.localStorage.removeItem(KEY);
	} catch (e) {
		// private mode or storage disabled: hearts last for this page only
	}
};

function serverMessage(body) {
	try {
		const messages = JSON.parse(body._server_messages || "[]");
		return JSON.parse(messages[messages.length - 1]).message;
	} catch (e) {
		return "";
	}
}

async function call(method, data) {
	const csrf = document.querySelector('meta[name="csrf-token"]');
	const response = await fetch(API + method, {
		method: data ? "POST" : "GET",
		headers: {
			Accept: "application/json",
			...(data ? { "Content-Type": "application/json" } : {}),
			...(csrf ? { "X-Frappe-CSRF-Token": csrf.content } : {}),
		},
		body: data ? JSON.stringify(data) : undefined,
	});
	const body = await response.json().catch(() => ({}));
	if (!response.ok) throw new Error(serverMessage(body));
	return body.message || [];
}

export function initWishlist() {
	const buttons = () => [...document.querySelectorAll("[data-wishlist]")];
	const loggedIn = () => Boolean(document.querySelector('meta[name="csrf-token"]'));
	const failed = () => document.querySelector("[data-wishlist]")?.dataset.failed || "";
	let saved = new Set();
	let loaded = false;

	const show = () =>
		buttons().forEach((button) => {
			button.hidden = false;
			button.setAttribute("aria-pressed", String(saved.has(button.dataset.wishlist)));
		});

	const load = async () => {
		if (!loggedIn()) {
			saved = new Set(readLocal());
			loaded = true;
			return show();
		}
		if (!buttons().length) return; // fetch only once there are hearts to show
		try {
			const local = readLocal();
			const list = local.length
				? await call("merge", { names: local })
				: await call("list_items");
			if (local.length) writeLocal([]); // only after the account has them
			saved = new Set(list);
			loaded = true;
			show();
		} catch (e) {
			// keep the hearts hidden when the account can't be reached
		}
	};

	document.addEventListener("click", async (event) => {
		const button = event.target.closest("[data-wishlist]");
		if (!button || !loaded) return;
		event.preventDefault();
		const name = button.dataset.wishlist;
		if (!loggedIn()) {
			saved = new Set(readLocal()); // another tab may have changed it
			const adding = !saved.has(name);
			if (adding && saved.size >= MAX) return toast(button.dataset.full || "");
			if (adding) saved.add(name);
			else saved.delete(name);
			writeLocal([...saved]);
			return show();
		}
		try {
			saved = new Set(
				await call(saved.has(name) ? "remove" : "add", { store_product: name })
			);
			show();
		} catch (e) {
			toast(e.message || failed());
		}
	});
	// listings swapped in by the filter island, and pages restored from the back/forward cache
	document.addEventListener("storefront:updated", () => (loaded ? show() : load()));
	window.addEventListener("pageshow", (event) => event.persisted && load());
	load();
}
